import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Check, Loader, Save, X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { updateMaterialityAssessmentReport } from './assessmentReportsEditApi.js';

const SCOPE_OPTIONS = ['In-Scope', 'Out-of-Scope'];
const SCOPE_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'In-Scope', label: 'In-Scope' },
  { value: 'Out-of-Scope', label: 'Out-of-Scope' },
];

/**
 * Entity Scoping conclusions edit on Assessment Reports.
 * Only Quantitative Conclusion, Qualitative Assessment, and Final Conclusion are editable.
 */
export default function EntityScopingConclusionsEditor({
  assessment,
  entityScopingData,
  selectedEntityKey,
  onSelectEntity,
  onSaved,
}) {
  const rows = entityScopingData?.entityScopingData || [];

  const [draftConclusions, setDraftConclusions] = useState({});
  const [draftQualitative, setDraftQualitative] = useState({});
  const [draftFinal, setDraftFinal] = useState({});
  const [editingQuantitative, setEditingQuantitative] = useState(null);
  const [editingQualitative, setEditingQualitative] = useState(null);
  const [editingFinal, setEditingFinal] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entitySearch, setEntitySearch] = useState('');
  const [quantitativeFilter, setQuantitativeFilter] = useState('');
  const [finalFilter, setFinalFilter] = useState('');
  const [sortKey, setSortKey] = useState(null); // 'entity' | 'revenue' | 'costs' | 'quantitative' | 'final'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  useEffect(() => {
    setDraftConclusions({ ...(entityScopingData?.entityConclusions || {}) });
    setDraftQualitative({ ...(entityScopingData?.qualitativeComments || {}) });
    setDraftFinal({ ...(entityScopingData?.finalConclusions || {}) });
    setDirty(false);
    setEditingQuantitative(null);
    setEditingQualitative(null);
    setEditingFinal(null);
    setEntitySearch('');
    setQuantitativeFilter('');
    setFinalFilter('');
    setSortKey(null);
    setSortDir('asc');
  }, [entityScopingData]);

  const isDirty = useMemo(() => dirty, [dirty]);

  const markDirty = () => setDirty(true);

  const resolveQuantitative = (row) =>
    draftConclusions[row.entity] || row.conclusion || 'Not Assessed';

  const resolveFinal = (row) =>
    draftFinal[row.entity] || resolveQuantitative(row);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'revenue' || key === 'costs' ? 'desc' : 'asc');
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />;
    }
    return sortDir === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 text-indigo-600" />
      : <ArrowDown className="h-3.5 w-3.5 text-indigo-600" />;
  };

  // Final In-Scope count (updates as user edits Final Conclusion)
  const finalInScopeCount = useMemo(
    () => rows.filter((row) => resolveFinal(row) === 'In-Scope').length,
    [rows, draftConclusions, draftFinal]
  );

  const displayedRows = useMemo(() => {
    const q = entitySearch.trim().toLowerCase();
    let list = rows.filter((row) => {
      if (q && !String(row.entity || '').toLowerCase().includes(q)) return false;
      const quantitative = resolveQuantitative(row);
      const final = resolveFinal(row);
      if (quantitativeFilter && quantitative !== quantitativeFilter) return false;
      if (finalFilter && final !== finalFilter) return false;
      return true;
    });

    if (!sortKey) return list;

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sortKey === 'entity') {
        return String(a.entity || '').localeCompare(String(b.entity || ''), undefined, {
          sensitivity: 'base',
          numeric: true,
        }) * dir;
      }
      if (sortKey === 'revenue') {
        return ((Number(a.revenue) || 0) - (Number(b.revenue) || 0)) * dir;
      }
      if (sortKey === 'costs') {
        return ((Number(a.costs) || 0) - (Number(b.costs) || 0)) * dir;
      }
      if (sortKey === 'quantitative') {
        return String(resolveQuantitative(a)).localeCompare(String(resolveQuantitative(b)), undefined, {
          sensitivity: 'base',
        }) * dir;
      }
      if (sortKey === 'final') {
        return String(resolveFinal(a)).localeCompare(String(resolveFinal(b)), undefined, {
          sensitivity: 'base',
        }) * dir;
      }
      return 0;
    });
    // resolve* use draft maps; include them so filter tracks edits
  }, [rows, entitySearch, quantitativeFilter, finalFilter, sortKey, sortDir, draftConclusions, draftFinal]);

  const handleCancel = () => {
    setDraftConclusions({ ...(entityScopingData?.entityConclusions || {}) });
    setDraftQualitative({ ...(entityScopingData?.qualitativeComments || {}) });
    setDraftFinal({ ...(entityScopingData?.finalConclusions || {}) });
    setDirty(false);
    setEditingQuantitative(null);
    setEditingQualitative(null);
    setEditingFinal(null);
  };

  const handleSave = async () => {
    if (!assessment?.id || !entityScopingData) return;
    try {
      setSaving(true);
      const nextEntityScoping = {
        ...entityScopingData,
        entityConclusions: { ...draftConclusions },
        qualitativeComments: { ...draftQualitative },
        finalConclusions: { ...draftFinal },
      };
      const payload = {
        analysis_name: assessment.analysis_name,
        documents_json: assessment.documents_json || null,
        materiality_json: assessment.materiality_json,
        sox_cycle_json: assessment.sox_cycle_json,
        entityscoping_json: nextEntityScoping,
        ...(assessment.audit_year_id ? { audit_year_id: assessment.audit_year_id } : {}),
      };
      const data = await updateMaterialityAssessmentReport(assessment.id, payload);
      if (!data?.success && !data?.id) {
        throw new Error(data?.error || 'Update failed');
      }
      toast.success('Entity scoping conclusions updated');
      setDirty(false);
      setEditingQuantitative(null);
      setEditingQualitative(null);
      setEditingFinal(null);
      onSaved?.({
        ...assessment,
        ...payload,
        id: assessment.id,
        entityscoping_json: nextEntityScoping,
      });
    } catch (err) {
      toast.error(
        resolve403ErrorMessage(err, err?.response?.data?.error || err.message || 'Failed to update')
      );
    } finally {
      setSaving(false);
    }
  };

  if (!rows.length) return null;

  const formatAmount = (amount) => {
    if (amount == null || Number.isNaN(amount)) return '$0.00';
    const isNegative = amount < 0;
    const formatted = Math.abs(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return isNegative ? `-$${formatted}` : `$${formatted}`;
  };

  return (
    <div>
      {/* Exact same Scoping Coverage Summary as assessment EntityScoping.js */}
      {Object.keys(draftFinal).length > 0 && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4">
          <h4 className="mb-3 text-sm font-semibold text-green-900">
            Scoping Coverage Summary (Based on Final Conclusions)
          </h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="mb-1 text-xs text-green-700">Revenue Coverage (In-Scope Entities)</p>
              <p className="text-lg font-bold text-green-900">
                {((rows
                  .filter((e) => (draftFinal[e.entity] || draftConclusions[e.entity] || e.conclusion) === 'In-Scope')
                  .reduce((sum, e) => sum + e.revenue, 0) /
                  rows.reduce((sum, e) => sum + e.revenue, 0)) * 100
                ).toFixed(1)}%
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs text-green-700">Cost Coverage (In-Scope Entities)</p>
              <p className="text-lg font-bold text-green-900">
                {((rows
                  .filter((e) => (draftFinal[e.entity] || draftConclusions[e.entity] || e.conclusion) === 'In-Scope')
                  .reduce((sum, e) => sum + e.costs, 0) /
                  rows.reduce((sum, e) => sum + e.costs, 0)) * 100
                ).toFixed(1)}%
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs text-green-700">In-Scope Entities (Final Conclusion)</p>
              <p className="text-lg font-bold text-green-900">{finalInScopeCount}</p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="entity-scoping-search" className="mb-1 block text-xs font-medium text-gray-600">
              Search entity
            </label>
            <input
              id="entity-scoping-search"
              type="search"
              value={entitySearch}
              onChange={(e) => setEntitySearch(e.target.value)}
              placeholder="Search entity…"
              className="w-48 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
          </div>
          <div>
            <label htmlFor="entity-scoping-quant-filter" className="mb-1 block text-xs font-medium text-gray-600">
              Quantitative Conclusion
            </label>
            <select
              id="entity-scoping-quant-filter"
              value={quantitativeFilter}
              onChange={(e) => setQuantitativeFilter(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              {SCOPE_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="entity-scoping-final-filter" className="mb-1 block text-xs font-medium text-gray-600">
              Final Conclusion
            </label>
            <select
              id="entity-scoping-final-filter"
              value={finalFilter}
              onChange={(e) => setFinalFilter(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              {SCOPE_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => {
              setEntitySearch('');
              setQuantitativeFilter('');
              setFinalFilter('');
              setSortKey(null);
              setSortDir('asc');
            }}
            disabled={!entitySearch && !quantitativeFilter && !finalFilter && !sortKey}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Reset
          </button>
        </div>
        <div className="flex items-center justify-end gap-2">
          {isDirty && (
            <>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Changes
              </button>
            </>
          )}
        </div>
      </div>

      <div className="overflow-auto max-h-[600px] rounded-lg border border-gray-200">
        <table className="w-full">
          <thead className="sticky top-0 z-30 border-b border-gray-200 bg-gray-50">
            <tr>
              <th className="sticky left-0 z-40 bg-gray-50 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                <button
                  type="button"
                  onClick={() => toggleSort('entity')}
                  className="inline-flex items-center gap-1 hover:text-indigo-700"
                >
                  Entity
                  <SortIcon column="entity" />
                </button>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-700">
                <button
                  type="button"
                  onClick={() => toggleSort('revenue')}
                  className="inline-flex items-center gap-1 hover:text-indigo-700"
                >
                  Revenue
                  <SortIcon column="revenue" />
                </button>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-700">
                <button
                  type="button"
                  onClick={() => toggleSort('costs')}
                  className="inline-flex items-center gap-1 hover:text-indigo-700"
                >
                  Costs
                  <SortIcon column="costs" />
                </button>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-700">% Revenue</th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-700">% Costs</th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-700">
                <button
                  type="button"
                  onClick={() => toggleSort('quantitative')}
                  className="inline-flex items-center gap-1 hover:text-indigo-700"
                >
                  Quantitative Conclusion
                  <SortIcon column="quantitative" />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">Qualitative Assessment</th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-700">
                <button
                  type="button"
                  onClick={() => toggleSort('final')}
                  className="inline-flex items-center gap-1 hover:text-indigo-700"
                >
                  Final Conclusion
                  <SortIcon column="final" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {displayedRows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">
                  No entities match the current search/filters.
                </td>
              </tr>
            ) : null}
            {displayedRows.map((row) => {
              const quantitative = resolveQuantitative(row);
              const qualitative = draftQualitative[row.entity] || '';
              const final = resolveFinal(row);

              return (
                <tr
                  key={row.entity}
                  className={`group transition-colors hover:bg-gray-50 ${
                    selectedEntityKey === row.entity ? 'bg-blue-50' : ''
                  }`}
                >
                  <td
                    className={`sticky left-0 z-10 cursor-pointer whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)] ${
                      selectedEntityKey === row.entity
                        ? 'bg-blue-50'
                        : 'bg-white group-hover:bg-gray-50'
                    }`}
                    onClick={() => onSelectEntity?.(row)}
                  >
                    {row.entity}
                  </td>
                  <td
                    className="cursor-pointer whitespace-nowrap px-6 py-4 text-right text-sm text-gray-900"
                    onClick={() => onSelectEntity?.(row)}
                  >
                    ${Number(row.revenue || 0).toLocaleString()}
                  </td>
                  <td
                    className="cursor-pointer whitespace-nowrap px-6 py-4 text-right text-sm text-gray-900"
                    onClick={() => onSelectEntity?.(row)}
                  >
                    ${Number(row.costs || 0).toLocaleString()}
                  </td>
                  <td
                    className="cursor-pointer whitespace-nowrap px-6 py-4 text-right text-sm text-gray-900"
                    onClick={() => onSelectEntity?.(row)}
                  >
                    {row.revPct}%
                  </td>
                  <td
                    className="cursor-pointer whitespace-nowrap px-6 py-4 text-right text-sm text-gray-900"
                    onClick={() => onSelectEntity?.(row)}
                  >
                    {row.costPct}%
                  </td>

                  {/* Quantitative Conclusion */}
                  <td className="whitespace-nowrap px-6 py-4 text-center text-sm" onClick={(e) => e.stopPropagation()}>
                    {editingQuantitative === row.entity ? (
                      <div className="flex items-center justify-center gap-2">
                        <select
                          value={quantitative === 'Not Assessed' ? 'Out-of-Scope' : quantitative}
                          onChange={(e) => {
                            setDraftConclusions((prev) => ({ ...prev, [row.entity]: e.target.value }));
                            markDirty();
                          }}
                          className="rounded-lg border border-blue-300 px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        >
                          {SCOPE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setEditingQuantitative(null)}
                          className="p-1 text-green-600 hover:text-green-700"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
                            quantitative === 'In-Scope'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {quantitative}
                        </span>
                        <button
                          type="button"
                          onClick={() => setEditingQuantitative(row.entity)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>

                  {/* Qualitative Assessment */}
                  <td className="px-6 py-4 text-sm text-gray-700" onClick={(e) => e.stopPropagation()}>
                    {editingQualitative === row.entity ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={qualitative}
                          onChange={(e) => {
                            setDraftQualitative((prev) => ({ ...prev, [row.entity]: e.target.value }));
                            markDirty();
                          }}
                          placeholder="Add qualitative assessment..."
                          className="flex-1 rounded-lg border border-blue-300 px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setEditingQualitative(null)}
                          className="p-1 text-green-600 hover:text-green-700"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="flex-1 text-sm text-gray-700">
                          {qualitative || <span className="italic text-gray-400">No assessment</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => setEditingQualitative(row.entity)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>

                  {/* Final Conclusion */}
                  <td className="whitespace-nowrap px-6 py-4 text-center text-sm" onClick={(e) => e.stopPropagation()}>
                    {editingFinal === row.entity ? (
                      <div className="flex items-center justify-center gap-2">
                        <select
                          value={final === 'Not Assessed' ? 'Out-of-Scope' : final}
                          onChange={(e) => {
                            setDraftFinal((prev) => ({ ...prev, [row.entity]: e.target.value }));
                            markDirty();
                          }}
                          className="rounded-lg border border-blue-300 px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        >
                          {SCOPE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setEditingFinal(null)}
                          className="p-1 text-green-600 hover:text-green-700"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
                            final === 'In-Scope'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {final}
                        </span>
                        <button
                          type="button"
                          onClick={() => setEditingFinal(row.entity)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-100 border-t-2 border-gray-300">
            <tr>
              <td className="sticky left-0 z-10 bg-gray-100 px-6 py-3 text-sm font-bold text-gray-900 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                Total ({rows.length})
              </td>
              <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                {formatAmount(rows.reduce((sum, e) => sum + (Number(e.revenue) || 0), 0))}
              </td>
              <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                {formatAmount(rows.reduce((sum, e) => sum + (Number(e.costs) || 0), 0))}
              </td>
              <td className="px-6 py-3 text-right text-sm font-bold text-gray-900"></td>
              <td className="px-6 py-3 text-right text-sm font-bold text-gray-900"></td>
              <td colSpan="3"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
